import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { MeetzyOrbComponent } from '../meetzy-orb/meetzy-orb.component';
import { collapseDiff, diffStats, lineDiff, type DiffChunk } from './prompt-diff';

/**
 * Aviso de "Meetzy ha estado trabajando para ti".
 *
 * Se monta en el shell de la app y, al entrar, pregunta si hay versiones
 * candidatas que este usuario todavía no ha visto. Las produce el bucle
 * nocturno a partir de los OK/KO y las notas que el equipo deja en /calls,
 * iterando contra el banco de tests del agente. La garantía NO es que pasen
 * todos los tests —los bancos suelen tener rojos previos— sino que la versión
 * no rompe ninguno de los que ya pasaban, y normalmente gana varios.
 *
 * El modal no despliega nada por su cuenta. La única acción que toca
 * producción es el botón "Desplegar", que es una de las dos puertas humanas
 * del bucle (la otra es dejar el propio OK/KO).
 *
 * Descartar pide un motivo (opcional): la siguiente pasada del bucle lo lee
 * para no volver a proponer el mismo enfoque. Si el agente cambió después de
 * prepararse la versión (`stale`), ya no se puede desplegar y se dice.
 */
@Component({
  selector: 'app-evolution-proposal-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MeetzyOrbComponent],
  templateUrl: './evolution-proposal-modal.component.html',
  styles: [`
    /* Pila tipográfica del sistema: en Mac resuelve a SF Pro, que es la razón
       de que los numerales grandes se lean como se leen. */
    .mz-modal { font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif; }

    .mz-card { box-shadow: 0 2px 8px rgba(16, 37, 28, .06), 0 32px 80px -12px rgba(16, 37, 28, .22); }

    /* Aurora de marca detrás del héroe. Se sale por arriba de la tarjeta a
       propósito (el overflow-hidden del contenedor la recorta) para que el
       degradado entre ya empezado y no se vea el borde del óvalo. */
    .mz-aurora {
      position: absolute;
      left: 50%;
      top: -170px;
      width: 640px;
      height: 420px;
      transform: translateX(-50%);
      pointer-events: none;
      background: radial-gradient(ellipse at 50% 60%,
        rgba(120, 214, 96, .11) 0%, rgba(14, 199, 154, .07) 38%, rgba(255, 255, 255, 0) 70%);
    }
    .mz-aurora--dark {
      background: radial-gradient(ellipse at 50% 60%,
        rgba(120, 214, 96, .09) 0%, rgba(14, 199, 154, .06) 38%, rgba(0, 0, 0, 0) 70%);
    }

    .mz-headline { font-size: 30px; line-height: 1.12; font-weight: 600; letter-spacing: -.028em; }

    .mz-display {
      font-size: 88px;
      line-height: 1;
      font-weight: 600;
      letter-spacing: -.045em;
      font-variant-numeric: tabular-nums;
    }
    /* La flecha pesa: con una fina había que comparar las dos cifras para
       deducir que una era mayor. Así la mejora se lee antes que los números. */
    .mz-bigarrow { width: 64px; height: 34px; margin-bottom: 26px; animation: mzNudge 3.4s ease-in-out infinite; }

    /* El denominador, mas pequeno y en el mismo verde: "0/5" se lee de un
       golpe como una fraccion, no como dos cifras sueltas. */
    .mz-display-unit { font-size: .5em; letter-spacing: -.02em; opacity: .75; }

    .mz-eyebrow {
      font-size: 10.5px;
      font-weight: 600;
      letter-spacing: .11em;
      text-transform: uppercase;
    }

    @keyframes mzNudge {
      0%, 72%, 100% { transform: translateX(0); }
      82%           { transform: translateX(4px); }
    }

    /* Verdes oscurecidos respecto al logo: los tonos de marca sobre blanco y a
       88px se lavan y el numeral pierde contraste justo donde más importa. */
    .mz-grad {
      background: linear-gradient(135deg, #5cae2e 0%, #1a9e5c 46%, #028a6b 100%);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }
    .mz-grad--dark {
      background: linear-gradient(135deg, #9fe05c 0%, #3fd08a 46%, #16c7a0 100%);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }

    .mz-cta {
      background: linear-gradient(135deg, #3fbf72 0%, #0fae86 100%);
      box-shadow: 0 1px 2px rgba(5, 90, 65, .28), 0 10px 24px -8px rgba(15, 174, 134, .55);
      transition: filter .15s ease, transform .15s ease;
    }
    .mz-cta:hover:not(:disabled) { filter: brightness(1.06); }
    .mz-cta:active:not(:disabled) { transform: translateY(1px); }

    .mz-quiet { color: #6b7772; transition: color .15s ease; }
    .mz-quiet:hover { color: #10251c; }
    .mz-quiet--dark { color: #8b968f; }
    .mz-quiet--dark:hover { color: #ffffff; }

    .mz-rise { animation: mzRise .55s cubic-bezier(.2, .8, .2, 1) both; }
    @keyframes mzRise {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: none; }
    }

    @media (prefers-reduced-motion: reduce) {
      .mz-rise, .mz-bigarrow { animation: none; }
    }
  `],
})
export class EvolutionProposalModalComponent implements OnInit {
  @Input() isDarkMode = false;

  proposals: any[] = [];
  index = 0;
  show = false;

  detail: any = null;
  showEvidence = false;
  showDiff = false;

  /** Sufijo para el degradado de la flecha: los ids de SVG son globales. */
  readonly uid = Math.random().toString(36).slice(2, 8);
  loadingDetail = false;
  deploying = false;
  discarding = false;
  /** Paso previo a descartar: se pide el motivo. */
  askingReason = false;
  discardReason = '';
  error = '';
  deployed = false;

  constructor(private api: ApiService, private router: Router) {}

  get current(): any | null {
    return this.proposals[this.index] || null;
  }

  get remaining(): number {
    return Math.max(0, this.proposals.length - this.index - 1);
  }

  /** Clase de riesgo del cambio (0-3), calculada en servidor desde el diff. */
  get riskClass(): number | null {
    const k = this.detail?.evidence?.risk?.class ?? this.current?.risk?.class;
    return typeof k === 'number' ? k : null;
  }

  riskLabel(): string {
    const labels: Record<number, string> = {
      0: 'Cosmetic change',
      1: 'Changes behaviour',
      2: 'Touches business data',
      3: 'Reaches outside',
    };
    return this.riskClass !== null ? labels[this.riskClass] || '' : '';
  }

  riskHint(): string {
    const hints: Record<number, string> = {
      0: 'Only punctuation, casing or spacing changed.',
      1: 'Changes what the agent says or how. Check it matches your tone.',
      2: 'New figures, prices, deadlines or schedules: make sure they are correct.',
      3: 'New phone numbers, links, emails or transfers: verify them before going live.',
    };
    return this.riskClass !== null ? hints[this.riskClass] || '' : '';
  }

  riskIsWarning(): boolean {
    return this.riskClass !== null && this.riskClass >= 2;
  }

  riskReasons(): string[] {
    const names: Record<string, string> = {
      phone: 'phone', url: 'link', email: 'email', external_action: 'transfer or send',
      money: 'price', percent: 'percentage', schedule: 'schedule or deadline',
    };
    const reasons = this.detail?.evidence?.risk?.reasons ?? this.current?.risk?.reasons ?? [];
    return reasons.map((r: any) => `${names[r.kind] || r.kind}: “${r.sample}”`);
  }

  /** El cambio exacto, línea a línea y plegado. Se calcula al abrirlo, no antes. */
  private diffCache: { key: string; chunks: DiffChunk[]; added: number; removed: number } | null = null;
  diff(): { chunks: DiffChunk[]; added: number; removed: number } | null {
    const before = this.detail?.base_prompt;
    const after = this.detail?.new_prompt;
    if (typeof before !== 'string' || typeof after !== 'string') return null;
    const key = `${before.length}:${after.length}:${this.current?.id}`;
    if (!this.diffCache || this.diffCache.key !== key) {
      const lines = lineDiff(before, after);
      const stats = diffStats(lines);
      this.diffCache = { key, chunks: collapseDiff(lines, 2), added: stats.added, removed: stats.removed };
    }
    return this.diffCache;
  }

  /** El agente cambió después de preparar la versión: ya no se puede desplegar tal cual. */
  get stale(): boolean {
    return Boolean(this.detail?.stale) || (this.detail?.status ? this.detail.status !== 'ready' : false);
  }

  async ngOnInit(): Promise<void> {
    try {
      const pending = await this.api.getEvolutionPending();
      this.proposals = (pending && pending.proposals) || [];
      if (this.proposals.length > 0) {
        this.show = true;
        await this.loadDetail();
      }
    } catch (_) {
      // Un fallo aquí no puede romper la entrada a la app: si no se puede
      // preguntar por propuestas, simplemente no hay aviso.
      this.show = false;
    }
  }

  private async loadDetail(): Promise<void> {
    const proposal = this.current;
    if (!proposal) return;
    this.loadingDetail = true;
    this.detail = null;
    this.error = '';
    this.showEvidence = false;
    this.showDiff = false;
    this.diffCache = null;
    this.askingReason = false;
    this.discardReason = '';
    try {
      this.detail = await this.api.getEvolutionProposal(proposal.id);
    } catch (_) {
      this.detail = null;
    } finally {
      this.loadingDetail = false;
    }
  }

  /** Cierra el aviso de esta propuesta sin decidir nada sobre ella. */
  async dismiss(): Promise<void> {
    const proposal = this.current;
    if (proposal) {
      this.api.markEvolutionProposalSeen(proposal.id).catch(() => {});
    }
    this.deployed = false;
    this.error = '';
    if (this.index < this.proposals.length - 1) {
      this.index += 1;
      await this.loadDetail();
    } else {
      this.show = false;
    }
  }

  /** Puerta humana nº2. */
  async deploy(): Promise<void> {
    const proposal = this.current;
    if (!proposal || this.deploying || this.stale) return;
    this.deploying = true;
    this.error = '';
    try {
      await this.api.deployEvolutionProposal(proposal.id);
      this.deployed = true;
      this.api.markEvolutionProposalSeen(proposal.id).catch(() => {});
    } catch (e: any) {
      this.error = e?.response?.data?.message || 'Could not deploy the version';
    } finally {
      this.deploying = false;
    }
  }

  beginDiscard(): void {
    this.askingReason = true;
  }

  cancelDiscard(): void {
    this.askingReason = false;
    this.discardReason = '';
  }

  async discard(): Promise<void> {
    const proposal = this.current;
    if (!proposal || this.discarding) return;
    this.discarding = true;
    try {
      await this.api.discardEvolutionProposal(proposal.id, this.discardReason);
    } catch (_) { /* el aviso se cierra igual */ }
    this.discarding = false;
    await this.dismiss();
  }

  /** Abre el agente en el editor avanzado para revisarlo con calma. */
  openInEditor(): void {
    const proposal = this.current;
    if (!proposal) return;
    this.api.markEvolutionProposalSeen(proposal.id).catch(() => {});
    this.show = false;
    this.router.navigate(['/advanced-editor', proposal.assistant_id]);
  }

  gained(proposal: any): number {
    return proposal?.tests?.gained || 0;
  }

  /** Tests que pasaba la version vigente: el "antes" del salto. */
  baseline(proposal: any): number {
    const tests = proposal?.tests;
    if (!tests) return 0;
    return Math.max(0, (tests.passed || 0) - (tests.gained || 0));
  }

  /**
   * Lo que el cliente quiere saber: que hara el agente distinto en la proxima
   * llamada. Lo escribe el modelo aparte de la frase tecnica (campo `impact`),
   * porque esa habla de secciones del prompt y aqui no pinta nada.
   */
  private impacts(): string[] {
    const list = this.detail?.evidence?.impacts;
    if (Array.isArray(list) && list.length > 0) {
      return list.filter((s: string) => String(s || '').trim().length > 0);
    }
    const summary = this.current?.summary;
    return summary ? [summary] : [];
  }

  headlineImpact(): string {
    return this.impacts()[0] || 'Meetzy has adjusted how the agent handles the calls you marked KO.';
  }

  /** El resto de mejoras. Con una sola, el titular ya la cuenta. */
  otherImpacts(): string[] {
    return this.impacts().slice(1);
  }

  /**
   * La prueba, en el idioma del cliente: "comprobaciones", no "tests". Dice lo
   * unico que hay que creerse — que esto se ha verificado y no se ha roto nada.
   */
  proofLine(proposal: any): string {
    const tests = proposal?.tests;
    const total = tests?.total ?? 0;
    const gained = this.gained(proposal);
    const rounds = tests?.rounds_used || 0;
    const effort = rounds > 1 ? ` Meetzy tried ${rounds} times before landing on this.` : '';

    if (gained > 0) {
      return `We ran the agent through its ${total} checks: it passes ${gained} more than the `
        + `version answering your calls today, and lost none of the ones it already passed.${effort}`;
    }
    return `We ran the agent through its ${total} checks and it lost none of the ones the `
      + `version answering your calls today already passed.${effort}`;
  }

  /** Que ha mejorado, una linea por mejora aceptada. */
  improvements(): string[] {
    const list = this.detail?.evidence?.improvements;
    if (Array.isArray(list) && list.length > 0) {
      return list.filter((s: string) => String(s || '').trim().length > 0);
    }
    const summary = this.current?.summary;
    return summary ? [summary] : ['Adjustment to the agent prompt.'];
  }

  /** Comprobaciones escritas por el bucle a partir de las notas del equipo. */
  newChecks(): string[] {
    const list = this.detail?.evidence?.new_tests;
    if (!Array.isArray(list)) return [];
    return list
      .map((t: any) => String(t?.name || '').replace(/\s*\[call:[^\]]+\]\s*$/, '').trim())
      .filter((s: string) => s.length > 0);
  }

  /** Rondas del bucle, para el desplegable. */
  rounds(): any[] {
    const list = this.detail?.tests?.rounds;
    return Array.isArray(list) ? list : [];
  }

  roundLabel(round: any): string {
    if (round?.accepted) {
      return `kept — ${round.passed}/${round.total} checks`
        + (round.summary ? ` · ${round.summary}` : '');
    }
    const why = round?.reason === 'does not add any passing test'
      ? 'did not add a passing check'
      : String(round?.reason || '').startsWith('breaks')
        ? 'broke something that already worked'
        : (round?.reason || 'no improvement');
    return `discarded — ${why}${round?.guard_skipped ? ' (rest of the suite skipped)' : ''}`;
  }

  /** Secciones del prompt que toca la version, para el desplegable. */
  sectionsTouched(): string[] {
    const changes = this.detail?.changes;
    if (!Array.isArray(changes)) return [];
    const seen = new Set<string>();
    for (const c of changes) {
      const section = String(c?.section || '').trim();
      if (section) seen.add(section);
    }
    return Array.from(seen).slice(0, 8);
  }

  /** Notas del equipo, sin repetidas. */
  evidenceNotes(): string[] {
    const notes = this.detail?.evidence?.notes || [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const n of notes) {
      const text = String(n?.note || '').trim();
      const key = text.toLowerCase();
      if (!text || seen.has(key)) continue;
      seen.add(key);
      out.push(text);
      if (out.length === 4) break;
    }
    return out;
  }

}
